import { useEffect } from 'react';
import MediaQuery from 'react-responsive';

import { BREAKPOINT_SIZES, BREAKPOINTS } from 'tradera/constants/breakpoints';
import {
    useTranslator,
    useUrlLocalizer,
    useCurrentLanguage
} from 'tradera/lang/translate';
import { MenuItemSeparator } from 'tradera/components/site-header/dropdown-menu-item-separator';
import MenuItemButton from 'tradera/components/site-header/dropdown-menu-item-button';
import { MenuItem } from 'tradera/components/site-header/dropdown-menu-item';
import { ExpandableMenuItem } from 'tradera/components/site-header/dropdown-menu-item-expandable';
import ALink from 'tradera/components/alink/alink';
import { RenderAsync } from 'tradera/components/suspense/render-async';
import EndingSoonIcon from '@tradera/blueprint/theme/icons2/branded-ending.svg?jsx';
import InfoIcon from '@tradera/blueprint/theme/icons2/info.svg?jsx';
import PenIcon from '@tradera/blueprint/theme/icons2/pen.svg?jsx';
import RecommendedIcon from '@tradera/blueprint/theme/icons2/branded-recommended.svg?jsx';
import BrandIcon from '@tradera/blueprint/theme/icons2/sale.svg?jsx';
import UserIcon from '@tradera/blueprint/theme/icons2/user.svg?jsx';
import CategoryCharityIcon from '@tradera/blueprint/theme/icons2/category-charity.svg?jsx';
import ValuationIcon from '@tradera/blueprint/theme/icons2/valuation.svg?jsx';
import InspirationIcon from '@tradera/blueprint/theme/icons2/inspiration.svg?jsx';
import { getCategoryIcon } from 'tradera/helpers/category-icon-helper';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { isSwedishOrUndefined } from 'tradera/localization/languagecode';
import { inspirationLinks } from './seo-links';
import { SearchInput } from 'tradera/components/form-controls/search-input/search-input';
import type { CategoryNode } from 'tradera/backend/data/category-data';
import { REDIRECT_TO_NEWDRAFT_QUERY } from 'tradera/apps/syi/constants';
import TrendingIcon from '@tradera/blueprint/theme/icons2/hot.svg?jsx';
import { useCategorySearch } from 'tradera/apps/tradera/hooks/use-category-search';
import { classNames } from 'tradera/utils/class-names';
import { useGetCategoriesQuery } from 'tradera/state/services/categories-api';
import styles from './dropdown-menu.module.scss';

type GaAttributes = {
    'data-ga-category': string;
    'data-ga-action': string;
    'data-ga-label': string;
};

type DropdownMenuContentProps = {
    categories?: { list: CategoryNode[]; topLevelCategories: CategoryNode[] };
    inSPA: boolean;
    isLoggedIn: boolean;
    onClick: () => void;
};

type ExtendedCategory = CategoryNode & {
    open?: boolean;
};

type CategoryMenuItemsProps = {
    inSPA: boolean;
    searchPlaceholder: string;
    onClick: () => void;
    isLoggedIn: boolean;
};

type CategoryMenuItemProps = {
    category: ExtendedCategory;
    inSPA?: boolean;
    onClick: () => void;
};

const getGaAttributes = (label: string): GaAttributes => ({
    'data-ga-category': 'Top navigation',
    'data-ga-action': 'Category menu',
    'data-ga-label': `Link - ${label}`
});

export const DropdownMenuContent = ({
    inSPA,
    isLoggedIn,
    onClick
}: DropdownMenuContentProps) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const isPennybridgeCharitiesEnabled = useIsFeatureEnabled(
        'pennybridge-charities'
    );

    const isTopListsEnabled = useIsFeatureEnabled('top-lists');

    const isValuationPageEnabled = useIsFeatureEnabled('valuation-page');
    const mediaProps = { maxWidth: BREAKPOINT_SIZES[BREAKPOINTS.MD].max };

    const { language } = useCurrentLanguage();

    const isSwedish = isSwedishOrUndefined(language);

    const inspirationLink =
        inspirationLinks[language as keyof typeof inspirationLinks] || '';

    return (
        <ul className="plain-list">
            {/* NOTE: Notifications are disabled for burger button for desktop due to hidden cart and messaging, see DropDownMenuHamburger */}
            <MediaQuery {...mediaProps}>
                {!isLoggedIn && (
                    <>
                        <ExpandableMenuItem
                            label={t('siteHeader_howTo_v2')}
                            icon={<InfoIcon className="icon icon-md" />}
                            border>
                            <MenuItem
                                className="pl-7"
                                label={t('siteHeader_howToBuy_v2')}
                                link={toLocalizedUrl('/how-to-buy')}
                                gaAttributes={getGaAttributes('How to buy')}
                            />
                            <MenuItem
                                className="pl-7"
                                label={t('siteHeader_howToSell_v2')}
                                link={toLocalizedUrl('/how-to-sell')}
                                gaAttributes={getGaAttributes('How to sell')}
                            />
                            {isSwedish && (
                                <MenuItem
                                    className="pl-7"
                                    label={t('siteHeader_howToSellCompany')}
                                    link={'https://business.tradera.com/'}
                                    gaAttributes={getGaAttributes(
                                        'How to sell company'
                                    )}
                                />
                            )}
                        </ExpandableMenuItem>
                        <MenuItem
                            border
                            gaAttributes={getGaAttributes('Signup')}
                            icon={<UserIcon className="icon icon-md" />}
                            inSPA={false}
                            label={t('siteHeader_register')}
                            link="/register"
                            onClick={onClick}
                        />
                        <MenuItem
                            border
                            gaAttributes={getGaAttributes('Create new listing')}
                            icon={<PenIcon className="icon icon-md" />}
                            inSPA={false}
                            label={t('siteHeader_createItem')}
                            link={toLocalizedUrl(
                                `/selling?${REDIRECT_TO_NEWDRAFT_QUERY}`
                            )}
                            onClick={onClick}
                        />
                    </>
                )}
            </MediaQuery>

            <MenuItem
                border
                follow={false}
                gaAttributes={getGaAttributes('Trending')}
                icon={<RecommendedIcon className="icon icon-md" />}
                inSPA={false}
                label={
                    isLoggedIn
                        ? t('siteHeader_recommendedForYou')
                        : t('siteHeader_recommendedAnonymous')
                }
                link={toLocalizedUrl('/ma/endless-recommendation/selected')}
                onClick={onClick}
            />
            <MenuItem
                border
                gaAttributes={getGaAttributes('Ending soon')}
                icon={<EndingSoonIcon className="icon icon-md" />}
                inSPA={inSPA}
                label={t('siteHeader_lastChance')}
                link={toLocalizedUrl('/campaign/sista-chansen?sortBy=TimeLeft')}
                onClick={onClick}
            />
            <MenuItem
                border
                gaAttributes={getGaAttributes('Brands')}
                icon={<BrandIcon className="icon icon-md" />}
                inSPA={false}
                label={t('siteHeader_brands')}
                link={toLocalizedUrl('/brands')}
                onClick={onClick}
            />
            {isPennybridgeCharitiesEnabled && (
                <MenuItem
                    border
                    gaAttributes={getGaAttributes('Donate')}
                    icon={<CategoryCharityIcon className="icon icon-md" />}
                    inSPA={inSPA}
                    label={t('siteHeader_donateWithTradera')}
                    link={toLocalizedUrl('/causes')}
                    onClick={onClick}
                />
            )}
            {isValuationPageEnabled && (
                <MenuItem
                    border
                    gaAttributes={getGaAttributes('Priskollen')}
                    icon={<ValuationIcon className="icon icon-md" />}
                    inSPA={inSPA}
                    label={t('siteHeader_valuation')}
                    link={toLocalizedUrl('/valuation')}
                    onClick={onClick}
                />
            )}
            {isTopListsEnabled && (
                <MenuItem
                    border
                    className="site-dropdown__item-with-icon"
                    gaAttributes={getGaAttributes('Trending on Tradera')}
                    icon={<TrendingIcon className="icon icon-md" />}
                    inSPA={inSPA}
                    label={t('siteHeader_trendingOnTradera')}
                    link={toLocalizedUrl('/top-lists')}
                    onClick={onClick}
                />
            )}
            <MenuItemSeparator className="d-block pt-1" />
            <MenuItemSeparator
                label={
                    <span className="d-block pt-2 bg-white text-left">
                        <ALink
                            useLink={inSPA}
                            href={toLocalizedUrl('/categories')}
                            {...getGaAttributes('Alla kategorier From top')}
                            className="text-dark pl-2 font-hansen font-weight-bold"
                            onClick={onClick}>
                            {t('siteHeader_categoriesAll')}
                        </ALink>
                    </span>
                }
            />
            <RenderAsync>
                <CategoryMenuItems
                    isLoggedIn={isLoggedIn}
                    inSPA={inSPA}
                    searchPlaceholder={t('siteHeader_searchAmongCategories')}
                    onClick={onClick}
                />
                {!isLoggedIn && inspirationLink && (
                    <MenuItem
                        border
                        follow={true}
                        gaAttributes={getGaAttributes('Inspiration')}
                        icon={<InspirationIcon className="icon icon-md" />}
                        inSPA={false}
                        label={t('siteHeader_inspiration')}
                        link={inspirationLink}
                        onClick={onClick}
                    />
                )}
                {/* NOTE: Fix for iPhone menu making bottom element unclickable */}
                <MediaQuery {...mediaProps}>
                    <MenuItemSeparator
                        className={styles.siteDropdownSeparatorBottom}
                    />
                </MediaQuery>
            </RenderAsync>
        </ul>
    );
};

const CategoryMenuItems = ({
    inSPA,
    searchPlaceholder,
    onClick,
    isLoggedIn
}: CategoryMenuItemsProps) => {
    const { data: categories } = useGetCategoriesQuery({
        level: 2
    });
    // topLevelCategories should have been fetched on the server, so use cache here until L2 categories have been fetched
    const { data: topLevelCategories } = useGetCategoriesQuery(
        { level: 1 },
        {
            // Categories list page will already have fetched L2 categories
            skip: Boolean(categories)
        }
    );

    const sourceCategories = categories ? categories : topLevelCategories;
    // Only show rebate codes for non logged in users.
    const categoryList = (sourceCategories || [])
        .map((category: CategoryNode) => ({ ...category, open: false }))
        .filter(
            (category: CategoryNode) =>
                !isLoggedIn || (isLoggedIn && category.id !== 0)
        );

    const {
        filter,
        setFilter,
        filteredCategories,
        handleFilterChange,
        handleFilterKey
    } = useCategorySearch(categoryList);

    useEffect(() => {
        if (categoryList[0]?.children !== filteredCategories[0]?.children) {
            setFilter(filter);
        }
        // We only want to run this when the categoryList changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryList, filter]);

    return (
        <>
            <MenuItemSeparator
                label={
                    <div className="col-12 offset-0 col-lg-auto mt-1 mb-2 px-lg-0 mx-lg-2 px-2 bg-white">
                        <SearchInput
                            placeholder={searchPlaceholder}
                            allowClear
                            value={filter}
                            onChange={handleFilterChange}
                            onKeyDown={handleFilterKey}
                        />
                    </div>
                }
            />
            {filteredCategories.map(
                (category) =>
                    category && (
                        <CategoryMenuItem
                            key={category.id}
                            category={category}
                            inSPA={category.id !== 0 && inSPA}
                            onClick={onClick}
                        />
                    )
            )}
        </>
    );
};

const CategoryMenuItem = ({
    category: { id, name, children, open },
    inSPA = false,
    onClick
}: CategoryMenuItemProps) => {
    const { t } = useTranslator();
    const href = `/category/${id}`;

    if (!children) {
        return (
            <MenuItem
                className={styles.itemWithImage}
                link={href}
                imageContainerClassName="mr-1"
                label={name}
                icon={getCategoryIcon(id, name)}
                border
            />
        );
    }
    return (
        <ExpandableMenuItem
            defaultExpanded={open}
            className={styles.itemWithImage}
            href={href}
            imageContainerClassName="mr-1"
            label={name}
            icon={getCategoryIcon(id, name)}
            border>
            <MenuItemButton
                key={href}
                className="mt-1 mw-100"
                gaAttributes={getGaAttributes(name)}
                inSPA={inSPA}
                label={t('siteHeader_categories_allWithin', {
                    category: name
                })}
                link={href}
                onClick={onClick}
            />

            {children.map((child) => {
                const href = `/category/${child.id}`;

                return (
                    <MenuItem
                        key={href}
                        bold={child.level === 1}
                        className={classNames('pl-5', styles.itemDefault)}
                        gaAttributes={getGaAttributes(
                            child.level === 1 ? name : `${name} > ${child.name}`
                        )}
                        inSPA={inSPA}
                        label={child.name}
                        link={href}
                        onClick={onClick}
                    />
                );
            })}
        </ExpandableMenuItem>
    );
};
